import { ActionTree, MutationTree } from "vuex";
import axios, { AxiosResponse } from "axios";
import { GiftCard } from "@/models/gift-cards/GiftCard";
import { GiftCardsState } from "@/models/store/GiftCardsState";
import { RootState } from "@/models/store/RootState";

export const state = {
  eligibleCards: [],
  issuedCards: [],
  loadingState: []
};

export const actions: ActionTree<GiftCardsState, RootState> = {
  getEligibleCards: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting eligible gift cards");
    return axios.get(`/gift-cards/reward-eligible`)
      .then((resp: AxiosResponse) => {
        commit("setEligibleCards", resp.data);
        commit("removeFromLoadingState", "getting eligible gift cards");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting eligible gift cards");
        return Promise.reject(err);
      });
  },

  getIssuedCards: ({ commit, dispatch }) => {
    commit("addToLoadingState", "getting issued gift cards");
    return axios.get(`/gift-cards/reward-issued`)
      .then((resp: AxiosResponse) => {
        commit("setIssuedCards", resp.data);
        commit("removeFromLoadingState", "getting issued gift cards");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting issued gift cards");
        return Promise.reject(err);
      });
  },

  handleGiftCard: ({ commit, dispatch }, payload) => {
    commit("addToLoadingState", "creating gift card reward");
    return axios.post(`/gift-cards/${payload.id}/issue-reward`, payload)
      .then((resp: AxiosResponse) => {
        dispatch("getEligibleCards");
        dispatch("getIssuedCards");
        dispatch("snackbar/successMessage", `Gift Card reward entered!`, { root: true });
        commit("removeFromLoadingState", "creating gift card reward");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "creating gift card reward");
        return Promise.reject(err);
      });
  },

  bulkIssueReward: ({ commit, dispatch }, payload) => {
    commit("addToLoadingState", "issuing bulk gift card reward");
    return axios.post(`/gift-cards/bulk-issue-reward`, payload)
      .then((resp: AxiosResponse) => {
        dispatch("getEligibleCards");
        dispatch("getIssuedCards");
        dispatch("snackbar/successMessage", `Reward confirmation saved`, { root: true });
        commit("removeFromLoadingState", "issuing bulk gift card reward");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "issuing bulk gift card reward");
        return Promise.reject(err);
      });
  },

  downloadGiftCardCsv: ({ commit, dispatch }) => {
    commit("addToLoadingState", "downloading eligible gift cards csv");
    return axios.get(`/gift-cards/reward-eligible/download`, {
      responseType: "blob"
    })
      .then((resp: AxiosResponse) => {
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "export-reward-eligible.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch("snackbar/successMessage", `Successfully downloaded Eligible Gift Cards CSV`, { root: true });
        commit("removeFromLoadingState", "downloading eligible gift cards csv");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", "Could not download eligible gift card CSV", { root: true });
        commit("removeFromLoadingState", "downloading eligible gift cards csv");
        return Promise.reject(err);
      });
  },

  downloadIssuedGiftCardCsv: ({ commit, dispatch }) => {
    commit("addToLoadingState", "downloading issued gift cards csv");
    return axios.get(`/gift-cards/reward-issued/download`, {
      responseType: "blob"
    })
      .then((resp: AxiosResponse) => {
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "export-reward-issued.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch("snackbar/successMessage", `Successfully downloaded Issued Gift Cards CSV`, { root: true });
        commit("removeFromLoadingState", "downloading issued gift cards csv");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", "Could not download issued gift card CSV", { root: true });
        commit("removeFromLoadingState", "downloading issued gift cards csv");
        return Promise.reject(err);
      });
  }
};

export const mutations: MutationTree<GiftCardsState> = {
  addToLoadingState: (state: GiftCardsState, message: string) => {
    state.loadingState.push(message);
  },
  removeFromLoadingState: (state: GiftCardsState, message: string) => {
    state.loadingState = state.loadingState.filter(loadingMessage => loadingMessage !== message);
  },
  setEligibleCards: (state: GiftCardsState, data: GiftCard[]) => {
    state.eligibleCards = data;
  },
  setIssuedCards: (state: GiftCardsState, data: GiftCard[]) => {
    state.issuedCards = data;
  }
};

export default {
  state,
  actions,
  mutations
};
